import {useFreelancerPayments} from '@features/payments/queries/useFreelancerPayments.ts'
import {Skeleton} from '@components/ui/skeleton'
import {AirbnbIcon, AlertTriangleIcon} from '@components/ui/icon'
import {formatPaymentAmount} from '@features/payments/utils.ts'
import {capitalize, cn} from '@utilities/helpers.ts'
import {ErrorBox} from '@components/commons/error-box'
import {useTranslation} from '@/translations/i18n.tsx'
import {ButtonLink} from '@components/commons/button-link'
import {UPWORK_URL} from '../constants'

const accordionBodyClassName = 'pt-0 pr-11 pb-3 pl-5 md:pr-13 md:pl-11'

const transactionClassName =
    'grid grid-cols-2 items-center py-3 text-xs sm:text-sm [&:not(:last-child)]:border-b [&:not(:last-child)]:border-neutral-300 [&_hgroup]:flex [&_hgroup]:max-w-full [&_hgroup]:flex-col [&_hgroup]:gap-0.5 [&_hgroup]:overflow-hidden [&_hgroup_h3]:text-xs [&_hgroup_h3]:font-medium sm:[&_hgroup_h3]:text-sm'

const airbnbLinkClassName =
    'flex items-center gap-2 text-xs font-normal text-neutral-500 md:text-sm [&_a]:cursor-pointer [&_a]:overflow-hidden [&_a]:text-ellipsis [&_a]:text-neutral-500 [&_a]:underline'

const getAmountClassName = (type: 'positive' | 'negative' | 'neutral') =>
    cn(
        'justify-self-end text-right font-medium whitespace-nowrap',
        type == 'positive' ? 'text-success-600' : type == 'negative' ? 'text-danger-600' : 'text-neutral-900'
    )

export const PaymentsAccordionBody = ({paymentDate}: {paymentDate: string}) => {
    const {t} = useTranslation()
    const freelancerPaymentsQuery = useFreelancerPayments(paymentDate)

    if (freelancerPaymentsQuery.isPending) {
        return (
            <div className={accordionBodyClassName}>
                <div className={transactionClassName}>
                    <hgroup>
                        <h3>
                            <Skeleton width={80} height={20} />
                        </h3>
                        <span className={airbnbLinkClassName}>
                            <Skeleton width={120} height={20} />
                        </span>
                    </hgroup>
                    <span className={getAmountClassName('neutral')}>
                        <Skeleton width={80} height={20} />
                    </span>
                </div>
            </div>
        )
    }
    if (freelancerPaymentsQuery.isError) {
        return (
            <div className={accordionBodyClassName}>
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            </div>
        )
    }

    return (
        <div className={accordionBodyClassName}>
            {freelancerPaymentsQuery.data.map((payment, index) => {
                const totalPaidOut = payment.payout_amount
                const type = totalPaidOut == 0 ? 'neutral' : totalPaidOut > 0 ? 'positive' : 'negative'

                return (
                    <div key={index} className={transactionClassName}>
                        <hgroup>
                            <h3>{capitalize(payment.payment_type)}</h3>
                            {payment.external_id && (
                                <span className={airbnbLinkClassName}>
                                    <AirbnbIcon size={16} />
                                    <a
                                        href={payment.airbnb_link}
                                        target="_blank"
                                        rel="noreferrer"
                                        title={payment.external_id}
                                    >
                                        {payment.external_id}
                                    </a>
                                </span>
                            )}
                        </hgroup>
                        <div className="flex flex-col items-end justify-end gap-2 md:flex-row md:items-stretch md:gap-4">
                            {payment?.is_paid_via_upwork ? (
                                <ButtonLink
                                    to={UPWORK_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="secondary"
                                    size="sm"
                                >
                                    {t('assignment:go_to_upwork_cta')}
                                </ButtonLink>
                            ) : (
                                <span className={getAmountClassName(type)}>
                                    {formatPaymentAmount({
                                        amount: payment.payout_photographer_currency_amount,
                                        amountUsd: payment.payout_amount,
                                        currency: payment.payout_photographer_currency,
                                        prefix: type == 'positive' ? '+' : ''
                                    })}
                                </span>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
